//#region Imports
import { AUTH_HEADER, createMetaHeaders, SETTINGS_API_ORIGIN_URL } from "@anydo/core";
import type { BoardPermissionLevel, IBoardRecord, ISpaceRecord, PositionT } from "@anydo/interfaces";
import { ICompositeBoardResponse } from "./types";
//#endregion

export async function fetchSyncHttpCreateBoardFromTemplate({
  authToken,
  spaceId,
  id,
  name,
  position,
  members,
  templateDataKey,
  isPrivate = false,
  spaceInvites = [],
  description = "",
  excludeCardsFromTemplateData = false,
  signal,
}: {
  authToken: string | null;
  spaceId: ISpaceRecord["id"];
  id: string;
  name?: string;
  position: PositionT;
  members: ReadonlyMap<string, BoardPermissionLevel>;
  templateDataKey: string;
  isPrivate?: boolean;
  spaceInvites?: string[]; // emails
  description?: string;
  excludeCardsFromTemplateData?: boolean;
  signal?: AbortSignal;
}): Promise<ICompositeBoardResponse> {
  const url = SETTINGS_API_ORIGIN_URL + "/me/boards/create_from_template";

  if (authToken == null) {
    throw new Error("fetchSyncHttpCreateBoardFromTemplate(): Cannot create board without auth token");
  }

  const members_ = Array.from(members.entries()).map(([memberPuid, permission]) => ({
    memberPuid,
    permission,
  }));

  const body: Pick<IBoardRecord, "spaceId" | "description" | "isPrivate" | "position"> & {
    members: { memberPuid: string; permission: BoardPermissionLevel }[];
    spaceInvites: string[];
    templateDataKey: string;
    boardId: IBoardRecord["id"];
    boardName?: IBoardRecord["name"];
    excludeCardsFromTemplateData: boolean;
    includeDescendantsInResponse: boolean;
  } = {
    boardId: id,
    spaceId,
    boardName: name,
    members: members_,
    spaceInvites,
    templateDataKey,
    description,
    isPrivate,
    position,
    excludeCardsFromTemplateData,
    includeDescendantsInResponse: true,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      [AUTH_HEADER]: authToken,
      ...createMetaHeaders(),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error(`fetchSyncHttpCreateBoardFromTemplate(): ${response.status} - ${await response.text()}`);
  }

  return response.json() as Promise<ICompositeBoardResponse>;
}
